import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, CornerDownLeft, Building2, Eye } from 'lucide-react'
import { apiGet } from '@claritio/frontend/lib/api'
import { useOrgId } from '@claritio/frontend/lib/auth'
import { recentOrgs, pushRecentOrg, type RecentOrg } from '@claritio/frontend/lib/recentOrgs'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@claritio/frontend/components/ui/command'
import OrgBadge from '@claritio/frontend/components/ui/OrgBadge'
import type { ClientPickerList, ClientPickerRow } from '@claritio/frontend/types'
import { SHELL_ROLES } from '@claritio/frontend/lib/permissions'
import type { ViewAsState } from '@claritio/frontend/lib/useViewAs'

function ClientRow({ c, current, onSelect }: { c: SwitcherClient; current: boolean; onSelect: (c: SwitcherClient) => void }) {
  return (
    // No keywords prop — cmdk concatenates it with value for scoring, and the
    // duplicated name lets queries subsequence-match across the seam.
    <CommandItem value={`${c.name}-${c.id}`} onSelect={() => onSelect(c)}>
      <OrgBadge name={c.name} size={24} />
      <span className="min-w-0 flex-1 truncate">{c.name}</span>
      {current && <Check className="size-4 shrink-0 text-muted-foreground" />}
    </CommandItem>
  )
}

// Recents carry {id, name}; picker rows add last_activity.
type SwitcherClient = RecentOrg | ClientPickerRow

// ENG-116: command-palette client switcher. Centered dialog (per mockup), one
// instance mounted in AppShell — opened by the sidebar switcher card, the
// Search nav item, and ⌘K. Recent (localStorage MRU) covers most real usage;
// everything else is fuzzy-searched, most-recently-active first.
// ENG-139: an optional onSelect turns it into a client picker (e.g. Home's
// "New assessment" flow) — the caller decides where the choice leads.
// Inside a client, a consultant also picks the role to preview here ("View as"):
// the preview belongs to the client, so it lives with the client choice.
export default function ClientSwitcher({ open, onOpenChange, onSelect, viewAs }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect?: (c: SwitcherClient) => void
  viewAs?: ViewAsState
}) {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const orgId = useOrgId()
  const [clients, setClients] = useState<ClientPickerRow[] | null>(null)
  const [query, setQuery] = useState('')

  // Adjusted during render rather than reset inside the effect.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) setQuery('')
  }

  useEffect(() => {
    if (!open) return
    apiGet<ClientPickerList>('/api/v1/clients/picker')
      .then((r) => setClients(r.clients))
      .catch(() => setClients([]))
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  if (!open) return null

  // From storage, not resolved against `clients` — that resolution is what made
  // Recent vanish whenever the list hadn't loaded or didn't contain the client.
  const recent = recentOrgs()
  const recentIds = new Set(recent.map((r) => r.id))
  const rest = (clients ?? [])
    .filter((c) => !recentIds.has(c.id))
    .sort((a, b) => {
      if (a.last_activity && b.last_activity) return b.last_activity.localeCompare(a.last_activity)
      if (a.last_activity !== b.last_activity) return a.last_activity ? -1 : 1
      return a.name.localeCompare(b.name)
    })

  function select(c: SwitcherClient) {
    pushRecentOrg({ id: c.id, name: c.name })
    onOpenChange(false)
    if (onSelect) return onSelect(c)
    navigate(`/chat?org_id=${c.id}`)
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-scrim/30" onClick={() => onOpenChange(false)} />
      <div className="absolute left-1/2 top-[18vh] w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border bg-popover shadow-[var(--shadow-card-hover)]">
        <Command loop>
          <CommandInput autoFocus placeholder={t('switcher.placeholder')} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{t('switcher.empty')}</CommandEmpty>
            {!query && orgId && viewAs?.canPreview && (
              <CommandGroup heading={t('shellNav.viewAs.label')}>
                {SHELL_ROLES.map((v) => (
                  <CommandItem key={v} value={`view-as-${v}`} onSelect={() => { viewAs.setRole(v); onOpenChange(false) }}>
                    <Eye className="size-4 text-muted-foreground" />
                    <span className="flex-1">{t(`shellNav.viewAs.${v}`)}</span>
                    {viewAs.role === v && <Check className="size-4 shrink-0 text-muted-foreground" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!query && (
              <CommandGroup>
                <CommandItem value="all-clients" onSelect={() => { onOpenChange(false); navigate('/clients') }}>
                  <Building2 className="size-4 text-muted-foreground" />
                  <span className="flex-1">{t('switcher.allClientsLink')}</span>
                </CommandItem>
              </CommandGroup>
            )}
            {recent.length > 0 && (
              <CommandGroup heading={t('switcher.recent')}>
                {recent.map((c) => <ClientRow key={c.id} c={c} current={String(c.id) === orgId} onSelect={select} />)}
              </CommandGroup>
            )}
            {rest.length > 0 && (
              <CommandGroup heading={query ? t('switcher.allMatches') : t('switcher.allClients')}>
                {rest.map((c) => <ClientRow key={c.id} c={c} current={String(c.id) === orgId} onSelect={select} />)}
              </CommandGroup>
            )}
          </CommandList>
          <div className="flex items-center gap-3 border-t px-3 py-1.5 text-caption text-muted-foreground">
            <span>↑↓ {t('switcher.navigate')}</span>
            <span className="flex items-center gap-1"><CornerDownLeft className="size-3" /> {t('switcher.select')}</span>
          </div>
        </Command>
      </div>
    </div>
  )
}
